/* Responsibility: Updates filter value for checkboxes */
import { parse, render } from 'mustache';
import dom from '../lib/dom.js';
import fn from '../lib/fn.js';
import preconditions from '../lib/preconditions.js';
import checkBoxFilterUtils from './modules/checkboxFilterUtils.js';

const checkPreconditions = preconditions('checkboxFilter', {
    root: 'element',
    template: 'string',
    resultViewCriteria: 'object',
    eventHub: 'object',
    dataKey: 'string',
    dataPreparer: 'function',
    urlKey: 'string',
    urlQueryParams: 'object',
});

export default function setupPaleCheckboxFilter(params) {
    checkPreconditions(params);
    const cbfn = checkBoxFilterUtils(params);
    parse(params.template);
    const renderTemplate = (data) => render(params.template, data);
    const filterNames = {};

    function updateResultViewCriteria() {
        cbfn.updateResultViewCriteria(params.resultViewCriteria, params.urlKey, cbfn.isUnchecked);
    }

    function uncheckFromOutsideEvent(box) {
        box.checked = false;
        params.eventHub.publish('filters:removed', { filterId: params.urlKey, itemId: box.value });
        updateResultViewCriteria();
    }

    function publishFiltersAdded(box) {
        params.eventHub.publish('filters:added', {
            filterId: params.urlKey,
            itemId: box.value,
            urlValue: box.value,
            urlKey: params.urlKey,
            text: params.filterTitle + filterNames[box.value],
            remove: uncheckFromOutsideEvent.bind(box, box),
        });
    }

    function publishFilterRemoved(val) {
        params.eventHub.publish('filters:removed', { filterId: params.urlKey, itemId: val });
    }

    function checkboxClicked(event) {
        const box = event.currentTarget;
        if (box.checked) {
            publishFiltersAdded(box);
        } else {
            publishFilterRemoved(box.value);
        }
        updateResultViewCriteria();
        params.eventHub.publish('resultViewCriteria:changed');
    }

    function updateChecked() {
        cbfn.eachCheckbox((input) => {
            input.checked =
                params.resultViewCriteria[params.urlKey] && fn.indexOf(input.value, params.resultViewCriteria[params.urlKey]) !== -1;
        });
    }

    function setStateFromUrlParams() {
        cbfn.setStateFromUrlParams(publishFiltersAdded, publishFilterRemoved, updateResultViewCriteria);
    }

    function renderElements(data, status) {
        if (status && status.firstResult) {
            params.dataPreparer(data);
            const facets = cbfn.flattenFacetsLists(data[params.dataKey]);
            fn.forEach(facets, (facet) => {
                if (facet.filterOverviewName) {
                    filterNames[facet.name] = facet.filterOverviewName;
                }
            });
            params.root.innerHTML = renderTemplate(data);
            setStateFromUrlParams();
            cbfn.eachCheckbox((box) => {
                cbfn.setState(box, cbfn.entryCountFromName(facets, box.value));
            });
        } else {
            const createElement = (original, updatedValues) => {
                const updated = updatedValues.find((element) => element.name === original.name);
                if (updated) {
                    return updated;
                }
                return { ...original, count: 0, hasElements: false };
            };

            const checkboxElements = [];
            if (params.dataKey === 'supplierFacet') {
                const charterElements = [];
                const nonCharterElements = [];
                FINN.travel.pp.initialResult[params.dataKey].charter.forEach((element) => {
                    charterElements.push(createElement(element, data[params.dataKey].charter));
                });
                FINN.travel.pp.initialResult[params.dataKey].nonCharter.forEach((element) => {
                    nonCharterElements.push(createElement(element, data[params.dataKey].nonCharter));
                });
                params.root.innerHTML = renderTemplate({ [params.dataKey]: { charter: charterElements, nonCharter: nonCharterElements } });
            } else {
                FINN.travel.pp.initialResult[params.dataKey].forEach((element) => {
                    checkboxElements.push(createElement(element, data[params.dataKey]));
                });
                if (params.dataKey === 'typeFacet') {
                    const friendlyName = {
                        uspesifisert: FINN.travel.lang.unspecified_lodging,
                        spesifisert: FINN.travel.lang.specified_lodging,
                        fly: FINN.travel.lang.flight_only,
                        enveisfly: FINN.travel.lang.one_way_flight,
                    };
                    checkboxElements.forEach((item) => {
                        item.text = friendlyName[item.name] || item.name;
                    });
                }
                params.root.innerHTML = renderTemplate({ [params.dataKey]: checkboxElements });
            }
            updateChecked();
        }
    }

    if (params.updateState) {
        params.updateState(params);
    }

    params.eventHub.subscribe('resultViewCriteria:changed', updateChecked);
    params.eventHub.subscribe('changed-results', renderElements);

    params.eventHub.subscribe('urlQueryParams:changed', setStateFromUrlParams);

    dom.on(params.root, 'click', 'input', checkboxClicked);
}
